/* tslint:disable */
import { Injectable } from '@angular/core';
import { HttpResponse, HttpContext, HttpParams } from '@angular/common/http';
import { TimelineBaseService } from '../timeline-base-service';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { PinnedBody } from '../models/pinned-body';
import { Post } from '../models/post';
import { PublishBody } from '../models/publish-body';
import { Count as LoopbackCount } from '../models/loopback/count';

@Injectable({ providedIn: 'root' })
export class TimelineControllerService extends TimelineBaseService {

  /**
   * Path part for operation timelineControllerGetPostsCount
   */
  static readonly TimelineControllerGetPostsCountPath = '/v1/posts/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPostsCount()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPostsCount$Response(params?: {
    where?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<LoopbackCount>> {

    const rb = new RequestBuilder(this.rootUrl, TimelineControllerService.TimelineControllerGetPostsCountPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LoopbackCount>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getPostsCount$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPostsCount(params?: {
    where?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<LoopbackCount> {

    return this.getPostsCount$Response(params).pipe(
      map((r: StrictHttpResponse<LoopbackCount>) => r.body as LoopbackCount)
    );
  }

  /**
   * Path part for operation timelineControllerGetPostsByEntityCount
   */
  static readonly TimelineControllerGetPostsByEntityCountPath = '/v1/posts/entities/{entityId}/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPostsByEntityCount()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPostsByEntityCount$Response(params: {
    entityId: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<LoopbackCount>> {

    const rb = new RequestBuilder(this.rootUrl, TimelineControllerService.TimelineControllerGetPostsByEntityCountPath, 'get');
    if (params) {
      rb.path('entityId', params.entityId, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LoopbackCount>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getPostsByEntityCount$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPostsByEntityCount(params: {
    entityId: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<LoopbackCount> {

    return this.getPostsByEntityCount$Response(params).pipe(
      map((r: StrictHttpResponse<LoopbackCount>) => r.body as LoopbackCount)
    );
  }

  /**
   * Path part for operation timelineControllerGetPostsByEntity
   */
  static readonly TimelineControllerGetPostsByEntityPath = '/v1/posts/entities/{entityId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPostsByEntity()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPostsByEntity$Response(params: {
    entityId: string;
    filter?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<Post>>> {

    const rb = new RequestBuilder(this.rootUrl, TimelineControllerService.TimelineControllerGetPostsByEntityPath, 'get');
    if (params) {
      rb.path('entityId', params.entityId, {});
      rb.query('filter', params.filter, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<Post>>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getPostsByEntity$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPostsByEntity(params: {
    entityId: string;
    filter?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<Post>> {

    return this.getPostsByEntity$Response(params).pipe(
      map((r: StrictHttpResponse<Array<Post>>) => r.body as Array<Post>)
    );
  }

  /**
   * Path part for operation timelineControllerPinnedPost
   */
  static readonly TimelineControllerPinnedPostPath = '/v1/posts/{id}/pin';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `pinnedPost()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  pinnedPost$Response(params: {
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext

    /**
     * Whether the post is pinned or not.
     */
    body?: PinnedBody
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, TimelineControllerService.TimelineControllerPinnedPostPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `pinnedPost$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  pinnedPost(params: {
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext

    /**
     * Whether the post is pinned or not.
     */
    body?: PinnedBody
  }): Observable<void> {

    return this.pinnedPost$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation timelineControllerPublishPost
   */
  static readonly TimelineControllerPublishPostPath = '/v1/posts/{id}/publish';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `publishPost()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  publishPost$Response(params: {
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext

    /**
     * Publish or un-publish a post
     */
    body?: PublishBody
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, TimelineControllerService.TimelineControllerPublishPostPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `publishPost$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  publishPost(params: {
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext

    /**
     * Publish or un-publish a post
     */
    body?: PublishBody
  }): Observable<void> {

    return this.publishPost$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation timelineControllerGetPostsById
   */
  static readonly TimelineControllerGetPostsByIdPath = '/v1/posts/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPostsById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPostsById$Response(params: {
    id: string;
    filter?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Post>> {

    const rb = new RequestBuilder(this.rootUrl, TimelineControllerService.TimelineControllerGetPostsByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Post>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getPostsById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPostsById(params: {
    id: string;
    filter?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Post> {

    return this.getPostsById$Response(params).pipe(
      map((r: StrictHttpResponse<Post>) => r.body as Post)
    );
  }

  /**
   * Path part for operation timelineControllerGetPosts
   */
  static readonly TimelineControllerGetPostsPath = '/v1/posts';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPosts()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPosts$Response(params?: {
    filter?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<Post>>> {

    const rb = new RequestBuilder(this.rootUrl, TimelineControllerService.TimelineControllerGetPostsPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<Post>>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getPosts$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPosts(params?: {
    filter?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<Post>> {

    return this.getPosts$Response(params).pipe(
      map((r: StrictHttpResponse<Array<Post>>) => r.body as Array<Post>)
    );
  }

}
